import React, { memo, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from 'antd';
import styled from 'styled-components';

import { IGroupOverview, useGroupRoster, useStaffUsers } from 'core/hooks';
import { Colors } from 'services/ColorService';

import { ICreateTaskDraft, PRIORITIES } from './taskBuckets';

export interface ITaskCreateSeed {
  /** Deadline column «+»: seed the due date. */
  dueDate?: string;
  /** Status column «+»: seed the status the task is created in. */
  status?: string;
  /** Preselect a group (rail selection / column context). */
  groupId?: string;
}

interface ITaskCreateModalProps {
  open: boolean;
  onClose: () => void;
  onCreate: (draft: ICreateTaskDraft) => Promise<boolean>;
  adding: boolean;
  /** Fixed group scope (group tab) — hides the group picker. */
  groupId?: string;
  /** Fixed student scope (student tab) — hides group + roster pickers. */
  studentId?: string;
  /** Group list for the dashboard's create picker. */
  groups?: IGroupOverview[];
  /** Default group when none is seeded (e.g. the command center's rail). */
  defaultGroupId?: string;
  seed?: ITaskCreateSeed;
}

/**
 * Create-task dialog: all fields laid out vertically in an antd Modal (mirrors
 * TaskFromMessageModal). Opened by the board's «＋ Добавить задачу» button or a
 * per-column «＋», which seeds the due date (deadline grouping) or status
 * (status grouping). Reuses the group / roster / assignee logic of the old
 * inline create form.
 */
export const TaskCreateModal = memo(
  ({
    open,
    onClose,
    onCreate,
    adding,
    groupId,
    studentId,
    groups,
    defaultGroupId,
    seed,
  }: ITaskCreateModalProps) => {
    const { t } = useTranslation();
    const isDashboard = !groupId && !studentId;

    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [formGroupId, setFormGroupId] = useState('');
    const [rosterRowId, setRosterRowId] = useState('');
    const [formDue, setFormDue] = useState('');
    const [priority, setPriority] = useState('normal');
    const [formAssigneeId, setFormAssigneeId] = useState('');
    const [seedStatus, setSeedStatus] = useState<string | undefined>(undefined);

    // Reset the form and apply the seed on every open.
    useEffect(() => {
      if (!open) return;
      setTitle('');
      setDescription('');
      setRosterRowId('');
      setFormAssigneeId('');
      setPriority('normal');
      setFormDue(seed?.dueDate || '');
      setSeedStatus(seed?.status);
      setFormGroupId(seed?.groupId || defaultGroupId || '');
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const { staffUsers } = useStaffUsers(false);
    const rosterGroupId = groupId || formGroupId;
    const { rows: roster } = useGroupRoster(rosterGroupId || undefined);
    const pickedRow = roster.find((r) => r?.id === rosterRowId);

    const createGroupId = groupId || formGroupId;
    const canCreate =
      Boolean(title.trim()) && (Boolean(studentId) || Boolean(createGroupId));

    const rosterLabel = (row: typeof roster[number]): string => {
      const name =
        [row?.student?.user?.lastName, row?.student?.user?.firstName]
          .filter(Boolean)
          .join(' ') || '—';
      return row?.agreementTitle ? `${name} · ${row.agreementTitle}` : name;
    };

    const onOk = useCallback(async () => {
      if (!canCreate || adding) return;
      const ok = await onCreate({
        title: title.trim(),
        description: description.trim() || undefined,
        groupId: createGroupId || undefined,
        studentId: studentId || pickedRow?.student?.id || undefined,
        agreementId: pickedRow?.id || undefined,
        assigneeId: formAssigneeId || undefined,
        dueDate: formDue || undefined,
        priority,
        status: seedStatus,
      });
      if (ok) onClose();
    }, [
      canCreate,
      adding,
      onCreate,
      title,
      description,
      createGroupId,
      studentId,
      pickedRow,
      formAssigneeId,
      formDue,
      priority,
      seedStatus,
      onClose,
    ]);

    return (
      <Modal
        visible={open}
        title={t('pages.tasks.createModalTitle')}
        onOk={onOk}
        onCancel={onClose}
        confirmLoading={adding}
        okText={t('pages.tasks.add')}
        cancelText={t('button.cancel')}
        okButtonProps={{ disabled: !canCreate }}
        destroyOnClose
      >
        <Field>
          <Label>{t('pages.communications.taskTitleLabel')}</Label>
          <TextInput
            autoFocus
            value={title}
            maxLength={255}
            placeholder={t('pages.dashboard.taskTitlePlaceholder')}
            onChange={(e) => setTitle(e.target.value)}
          />
        </Field>

        <Field>
          <Label>{t('pages.communications.taskDescriptionLabel')}</Label>
          <TextArea
            rows={4}
            value={description}
            placeholder={t('pages.tasks.descriptionPlaceholder')}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Field>

        {isDashboard ? (
          <Field>
            <Label>{t('pages.dashboard.col.group')}</Label>
            <SelectInput
              value={formGroupId}
              onChange={(e) => {
                setFormGroupId(e.target.value);
                setRosterRowId('');
              }}
            >
              <option value="">{t('pages.dashboard.pickGroup')}</option>
              {(groups || []).map((g) => (
                <option key={g.groupId} value={g.groupId}>
                  {g.name}
                </option>
              ))}
            </SelectInput>
          </Field>
        ) : null}

        {!studentId && rosterGroupId ? (
          <Field>
            <Label>{t('pages.tasks.drawer.student')}</Label>
            <SelectInput
              value={rosterRowId}
              onChange={(e) => setRosterRowId(e.target.value)}
            >
              <option value="">{t('pages.dashboard.wholeGroup')}</option>
              {roster.map((r) => (
                <option key={r?.id} value={r?.id}>
                  {rosterLabel(r)}
                </option>
              ))}
            </SelectInput>
          </Field>
        ) : null}

        <Row>
          <Field>
            <Label>{t('pages.tasks.drawer.due')}</Label>
            <TextInput
              type="date"
              value={formDue}
              onChange={(e) => setFormDue(e.target.value)}
            />
          </Field>
          <Field>
            <Label>{t('pages.communications.taskPriorityLabel')}</Label>
            <SelectInput
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
            >
              {PRIORITIES.map((p) => (
                <option key={p} value={p}>
                  {t(`pages.tasks.priority.${p}`)}
                </option>
              ))}
            </SelectInput>
          </Field>
        </Row>

        <Field>
          <Label>{t('pages.tasks.assignee')}</Label>
          <SelectInput
            value={formAssigneeId}
            onChange={(e) => setFormAssigneeId(e.target.value)}
          >
            <option value="">{t('pages.tasks.assigneeMe')}</option>
            {staffUsers.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name || u.email}
              </option>
            ))}
          </SelectInput>
        </Field>
      </Modal>
    );
  },
);

TaskCreateModal.displayName = 'TaskCreateModal';

const Field = styled.div`
  flex: 1;
  min-width: 0;
  margin-bottom: 12px;
`;

const Row = styled.div`
  display: flex;
  gap: 12px;
`;

const Label = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${Colors.Gray};
  margin-bottom: 4px;
`;

const TextInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  border: 1px solid ${Colors.SecondLightGray};
  border-radius: 8px;
  padding: 8px 10px;
  font: inherit;
  font-size: 13px;
`;

const TextArea = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  border: 1px solid ${Colors.SecondLightGray};
  border-radius: 8px;
  padding: 8px 10px;
  font: inherit;
  font-size: 13px;
  resize: vertical;
`;

const SelectInput = styled.select`
  width: 100%;
  box-sizing: border-box;
  border: 1px solid ${Colors.SecondLightGray};
  border-radius: 8px;
  padding: 8px;
  font: inherit;
  font-size: 13px;
  background: ${Colors.White};
`;
